import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import TagInput, { TagInputRef } from './TagInput';
import api from '../api/axiosConfig';
import { Tag } from '../types/authTypes';
import { useEditor, EditorContent, Extension, textblockTypeInputRule } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { useLanguage } from '../i18n/LanguageContext';
import { convertImageToPNG } from '../utils/imageUtils';
import { formatBytes, isPdfFile, openMedia } from '../utils/attachments';
import { usePremium } from '../hooks/usePremium';
import { useAutoTagging } from '../hooks/useAutoTagging';
import PremiumFeatureModal, { PremiumFeatureKey } from './PremiumFeatureModal';
import { findYoutubeUrl } from '../utils/youtube';
import { NoteCodeBlock, noteMathExtensions, insertInlineMath } from './noteEditorExtensions';
import { Access, Conflict, canEdit, conflictFrom, isOwner } from '../api/sharingApi';
import SharedBanner from './sharing/SharedBanner';
import ConflictDialog from './sharing/ConflictDialog';
import './NoteModal.css';

interface NoteModalProps {
  isOpen: boolean;
  noteId?: number | null; // null/undefined for create, number for edit
  onClose: () => void;
  onSuccess: () => void;
  // Pre-attached tags for create mode (e.g. the storyboard pane seeds the
  // tag the user just searched for)
  initialTags?: Tag[];
}

interface LinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  links: ExternalLink[];
  setLinks: (links: ExternalLink[]) => void;
}

interface ExternalLink {
  url: string;
  link_type: string;
}

const LinkModal: React.FC<LinkModalProps> = ({ isOpen, onClose, links, setLinks }) => {
  const { t } = useLanguage();
  const [currentUrl, setCurrentUrl] = useState('');
  const [currentType, setCurrentType] = useState('reference');

  if (!isOpen) return null;

  const addLink = () => {
    if (currentUrl.trim()) {
      setLinks([...links, { url: currentUrl.trim(), link_type: currentType }]);
      setCurrentUrl('');
    }
  };

  const removeLink = (index: number) => {
    setLinks(links.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addLink();
    }
  };

  return (
    <div className="link-modal-overlay" onClick={onClose}>
      <div className="link-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="link-modal-header">
          <h3>{t.links.sourceUrls}</h3>
          <button className="link-modal-close" onClick={onClose} aria-label={t.note.close}>×</button>
        </div>
        <div className="link-modal-body">
          <div className="link-input-row">
            <input
              type="url"
              value={currentUrl}
              onChange={(e) => setCurrentUrl(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t.links.enterUrl}
              className="link-url-input"
            />
            <select
              value={currentType}
              onChange={(e) => setCurrentType(e.target.value)}
              className="link-type-select"
            >
              <option value="reference">{t.links.typeReference}</option>
              <option value="video">{t.links.typeVideo}</option>
              <option value="meme">{t.links.typeMeme}</option>
            </select>
            <button
              type="button"
              onClick={addLink}
              className="add-link-btn"
              disabled={!currentUrl.trim()}
            >
              {t.links.add}
            </button>
          </div>
          <div className="links-list">
            {links.map((link, index) => (
              <div key={index} className="link-item">
                <span className="link-url">{link.url}</span>
                <span className={`link-type link-type-${link.link_type}`}>
                  {link.link_type}
                </span>
                <button
                  type="button"
                  onClick={() => removeLink(index)}
                  className="remove-link-btn"
                  aria-label={t.links.removeLink}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// StarterKit only opens a code block on ``` *followed by a space or a
// newline*, so typing the three backticks appears to do nothing. Slack and
// Jira open the box on the third backtick, which is what people expect and
// what the mobile composer does. This adds that rule; the built-in one still
// works, so ```bash and a space keeps its language word.
const InstantCodeFence = Extension.create({
  name: 'instantCodeFence',
  addInputRules() {
    const type = this.editor.schema.nodes.codeBlock;
    return type ? [textblockTypeInputRule({ find: /^```$/, type })] : [];
  },
});

const NoteModal: React.FC<NoteModalProps> = ({ isOpen, noteId, onClose, onSuccess, initialTags }) => {
  const { t, language } = useLanguage();
  const isEdit = noteId != null;
  
  const [content, setContent] = useState<string>('');
  const [tags, setTags] = useState<Tag[]>([]);
  const [media, setMedia] = useState<File | null>(null);
  // The attached image, shown under the text rather than inside the document.
  // It used to be an <img> node in the ProseMirror doc, swapped for the text
  // "[[image]]" on save — which meant the only way to take a picture off a
  // note was to select the node and press delete.
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageAspect, setImageAspect] = useState<number | null>(null);
  // A PDF already on the note being edited. It is never fetched or sent back:
  // the server keeps it unless the user takes it off.
  const [existingPdf, setExistingPdf] = useState<{ name: string; size: number; url: string } | null>(null);
  const [hadOriginalMedia, setHadOriginalMedia] = useState(false); // Track if note originally had an attachment
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExtractingText, setIsExtractingText] = useState(false);
  const { premium } = usePremium();
  const [premiumDemo, setPremiumDemo] = useState<PremiumFeatureKey | null>(null);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [links, setLinks] = useState<ExternalLink[]>([]);
  const [isGeneratingTags, setIsGeneratingTags] = useState(false);
  const [isSummarizingVideo, setIsSummarizingVideo] = useState(false);
  // On by default: a note saved with no tags is a note you will never find
  // again, so AI tagging is the safety net unless the user turns it off.
  // Kept on the account, so turning it off here keeps it off everywhere.
  const [useAiTags, setUseAiTags] = useAutoTagging();
  const [isLoading, setIsLoading] = useState(false);
  // Whose note this is. A note someone shared is opened here too: a viewer
  // reads it, an editor may change the words but not its tags.
  const [access, setAccess] = useState<Access | null>(null);
  const [conflict, setConflict] = useState<Conflict | null>(null);
  // The updated_at this modal loaded, sent back so a save over someone
  // else's newer one is caught rather than silently winning.
  const baseUpdatedAtRef = useRef<string | null>(null);
  const readOnly = !canEdit(access);
  const owned = isOwner(access);

  // Recomputed when the note changes, which is how the button enables itself
  // as the user types a URL. Memoised because `content` can carry a megabyte
  // of base64 image data that would otherwise be rescanned every render.
  const youtubeUrl = useMemo(() => findYoutubeUrl(links, content), [links, content]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const tagInputRef = useRef<TagInputRef>(null);

  // The modal is never unmounted — it renders null while closed — so every
  // piece of attachment state survives until something clears it. Routing all
  // preview changes through here keeps the blob URL of the picture we are
  // replacing from leaking for the life of the tab.
  const previewUrlRef = useRef<string | null>(null);
  const setPreview = useCallback((url: string | null) => {
    if (previewUrlRef.current && previewUrlRef.current !== url) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    previewUrlRef.current = url?.startsWith('blob:') ? url : null;
    setImagePreview(url);
  }, []);

  // Everything that says "this note has a picture". Called before loading a
  // note for editing as well: without it the composer kept the last image it
  // was shown, and saving re-uploaded that image onto a note that never had
  // one.
  const clearAttachment = useCallback(() => {
    setPreview(null);
    setImageAspect(null);
    setMedia(null);
    setExistingPdf(null);
  }, [setPreview]);

  // A PDF stands in the same single attachment slot a picture does, so
  // attaching one replaces whatever was there.
  const attachPdf = useCallback((file: File) => {
    clearAttachment();
    setMedia(file);
  }, [clearAttachment]);

  const newPdf = media && isPdfFile(media) ? media : null;
  const pdfAttachment = newPdf
    ? { name: newPdf.name, size: newPdf.size }
    : existingPdf;
  const hasAttachment = !!imagePreview || !!pdfAttachment;

  const handleToggleBulletList = () => {
    if (!editor) return;

    if (editor.isActive('bulletList')) {
      editor.chain().focus().toggleBulletList().run();
      return;
    }

    // Check if the selection contains hard breaks (Shift+Enter lines)
    const { state } = editor;
    const { from, to } = state.selection;
    let hasHardBreaks = false;
    state.doc.nodesBetween(from, to, (node) => {
      if (node.type.name === 'hardBreak') hasHardBreaks = true;
    });

    if (hasHardBreaks) {
      // Split on hard breaks and turn each line into its own list item
      const lines = state.doc.textBetween(from, to, '\n', '\n')
        .split('\n')
        .filter(line => line.trim());
      if (lines.length > 1) {
        const listHTML = `<ul>${lines.map(line => `<li><p>${line}</p></li>`).join('')}</ul>`;
        editor.chain().focus().deleteSelection().insertContent(listHTML).run();
        return;
      }
    }

    // Default: handles multi-paragraph selections natively
    editor.chain().focus().toggleBulletList().run();
  };

  // The math node views need the editor to open their edit prompt, and the
  // editor does not exist yet while its extension list is being built.
  const editorRef = useRef<ReturnType<typeof useEditor>>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false }),
      NoteCodeBlock,
      InstantCodeFence,
      ...noteMathExtensions(() => editorRef.current, t.addNote.latexPrompt),
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: {
          class: 'editor-image',
        },
      }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setContent(html);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none',
      },
      handlePaste: (view, event, slice) => {
        // Check if clipboard contains files (images)
        const items = Array.from(event.clipboardData?.items || []);
        // A PDF copied in Finder or Explorer arrives as a file
        const pdf = items.map(item => item.kind === 'file' ? item.getAsFile() : null)
          .find((file): file is File => !!file && isPdfFile(file));
        if (pdf) {
          event.preventDefault();
          attachPdf(pdf);
          return true;
        }
        const imageItem = items.find(item => item.type.startsWith('image/'));
        
        if (imageItem) {
          event.preventDefault();
          const file = imageItem.getAsFile();
          if (file) {
            // Convert to PNG and store in media state
            convertImageToPNG(file).then(processedFile => {
              setMedia(processedFile);
              // Pasting again replaces what is attached, which is what
              // pasting a corrected screenshot is meant to do.
              setImageAspect(null);
              setPreview(URL.createObjectURL(processedFile));
            }).catch(error => {
              console.error('Failed to process pasted image:', error);
              setError(t.addNote.pasteImageFailed);
            });
          }
          return true; // Handled
        }
        
        return false; // Let Tiptap handle other paste events
      },
      handleDrop: (view, event) => {
        const pdf = Array.from(event.dataTransfer?.files || []).find(isPdfFile);
        if (!pdf) return false;
        event.preventDefault();
        attachPdf(pdf);
        return true;
      },
    },
  });
  editorRef.current = editor;

  // Load note data for edit mode
  const loadNoteData = useCallback(async () => {
    if (!noteId || !editor) return;
    
    setIsLoading(true);
    // Start from an empty composer: whatever is on screen belongs to the note
    // that was open before this one.
    clearAttachment();
    setHadOriginalMedia(false);
    setError(null);
    try {
      const response = await api.get(`/entries/${noteId}/`);
      
      const note = response.data;
      
      setAccess(note.access || null);
      baseUpdatedAtRef.current = note.updated_at || null;
      setTags(note.tag_details || []);
      setLinks(note.links || []);
      
      // Legacy notes carry the literal "[[image]]" in their content; without
      // this the editor shows it as text the user then has to delete.
      let contentToSet = (note.content || '').replace(/\[\[image\]\]/g, '');
      
      // Track if note originally had an attachment
      setHadOriginalMedia(!!(note.media && note.media.file_url));

      if (note.media?.media_type === 'pdf' && note.media.file_url) {
        setExistingPdf({
          name: note.media.name || 'PDF',
          size: note.media.size || 0,
          url: note.media.file_url,
        });
      } else if (note.media && note.media.file_url) {
        // For images, fetch and convert to File object for OCR capability
        if (note.media.media_type === 'image') {
          try {
            const filename = note.media.file_url.split('/').pop()?.split('?')[0];
            const mediaResponse = await fetch(note.media.file_url);
            
            if (mediaResponse.ok) {
              const blob = await mediaResponse.blob();
              const extension = filename?.split('.').pop()?.toLowerCase();
              let mimeType = blob.type;
              
              // Infer mime type from extension if not provided
              if (!mimeType || mimeType === 'application/octet-stream') {
                if (extension && ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
                  mimeType = `image/${extension === 'jpg' ? 'jpeg' : extension}`;
                }
              }
              
              const mediaFile = new File([blob], filename || 'media', { type: mimeType });
              setMedia(mediaFile);

              setPreview(URL.createObjectURL(mediaFile));
            } else {
              console.error('Failed to fetch media, status:', mediaResponse.status);
              // Fallback: use S3 URL directly
              setPreview(note.media.file_url);
            }
          } catch (error) {
            console.error('Failed to fetch media for editing:', error);
            setPreview(note.media.file_url);
          }
        }
      }
      
      // Set content in editor without triggering onUpdate
      queueMicrotask(() => {
        editor.commands.setContent(contentToSet, { emitUpdate: false });
      });
      setContent(contentToSet);
    } catch (error) {
      console.error('Failed to load note:', error);
      setError(t.addNote.loadNoteFailed);
    } finally {
      setIsLoading(false);
    }
  }, [noteId, editor, clearAttachment, setPreview, t]);

  useEffect(() => {
    if (isOpen && isEdit && editor) {
      loadNoteData();
    }
  }, [isOpen, isEdit, editor, loadNoteData]);

  // Someone else's note that was shared read-only: the composer becomes a
  // reader rather than accepting typing the server would refuse.
  useEffect(() => {
    editor?.setEditable(!readOnly);
  }, [editor, readOnly]);

  useEffect(() => {
    if (isOpen && !isEdit && editor) {
      // Reset for add mode (seeding any tags the opener supplied)
      editor.commands.setContent('');
      setContent('');
      setTags(initialTags && initialTags.length > 0 ? initialTags : []);
      setAccess(null);
      setConflict(null);
      baseUpdatedAtRef.current = null;
      clearAttachment();
      setHadOriginalMedia(false);
      setLinks([]);
      setError(null);
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEdit, editor]);

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Cleared so picking the same file again, after removing it, still fires
    e.target.value = '';
    if (!file) return;

    if (isPdfFile(file)) {
      attachPdf(file);
      return;
    }

    try {
      let processedFile: File;
      
      if (file.type.startsWith('image/')) {
        processedFile = await convertImageToPNG(file);
        
        setImageAspect(null);
        setPreview(URL.createObjectURL(processedFile));
      } else if (file.type.startsWith('video/')) {
        processedFile = file;
        const videoUrl = URL.createObjectURL(file);
        if (editor) {
          const currentContent = editor.getHTML();
          editor.commands.setContent(`${currentContent}<p></p><video src="${videoUrl}" controls />`);
        }
      } else {
        setError(t.addNote.unsupportedFile);
        return;
      }
      
      setMedia(processedFile);
    } catch (error) {
      console.error('Error processing file:', error);
      setError(t.addNote.fileFailed);
    }
  };

  const extractTextFromImage = async () => {
    if (!media || !media.type.startsWith('image/')) return;
    if (!premium) { setPremiumDemo('ocr'); return; }

    setIsExtractingText(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', media);

      const response = await api.post('/extract-text/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      const extractedText = response.data.text;
      if (extractedText && editor) {
        // The picture stays attached; only its words join the note.
        editor.commands.setContent(`${editor.getHTML()}<p>${extractedText}</p>`);
      }
    } catch (error) {
      console.error('OCR extraction failed:', error);
      setError(t.addNote.ocrFailed);
    } finally {
      setIsExtractingText(false);
    }
  };

  /**
   * Append a summary of the note's YouTube video to the note body.
   *
   * The summary becomes ordinary note text on purpose: it is searchable, the
   * user can edit or delete it, and it travels with the note into documents.
   */
  const summarizeVideo = async () => {
    if (!editor || !youtubeUrl || isSummarizingVideo) return;
    if (!premium) { setPremiumDemo('video_summary'); return; }

    setIsSummarizingVideo(true);
    setError(null);

    try {
      const response = await api.post('/summarize-video/', {
        url: youtubeUrl,
        language,
      });

      const summaryHtml = response.data.summary_html;
      if (summaryHtml) {
        // Appended rather than replacing: whatever the user already wrote
        // about the video is theirs and worth more than the summary.
        editor.chain().focus('end').insertContent(
          `<h3>${t.addNote.videoSummaryHeading}</h3>${summaryHtml}`
        ).run();
      }
    } catch (err: any) {
      if (err.response?.status === 402) {
        setPremiumDemo('video_summary');
      } else {
        setError(err.response?.data?.error || t.addNote.videoSummaryFailed);
      }
    } finally {
      setIsSummarizingVideo(false);
    }
  };

  const suggestTagsWithAI = async (): Promise<Tag[]> => {
    if (!editor) return [];
    
    const currentContent = editor.getText();
    
    // A new PDF is something to tag by even with nothing typed: the server
    // reads its opening pages.
    if ((!currentContent || currentContent.trim() === '') && !newPdf) {
      setError(t.addNote.addContentFirst);
      return [];
    }

    setIsGeneratingTags(true);
    setError(null);

    try {
      let response;
      if (newPdf) {
        const formData = new FormData();
        formData.append('content', currentContent);
        formData.append('pdf', newPdf);
        response = await api.post('/suggest-tags/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        response = await api.post('/suggest-tags/',
          { content: currentContent },
          {
            headers: {
              'Content-Type': 'application/json',
            }
          }
        );
      }

      return response.data.tags || [];
    } catch (error) {
      console.error('Tag suggestion failed:', error);
      setError(t.addNote.tagSuggestionFailed);
      return [];
    } finally {
      setIsGeneratingTags(false);
    }
  };

  const handleSubmit = async () => {
    if (isEdit && !noteId) return;
    
    setError(null);
    
    // The attachment is the source of truth for whether there is a picture.
    // This used to look for an <img> in the document, which is no longer
    // where the image lives.
    if (!content || content.trim() === '' || content === '<p></p>') {
      if (!hasAttachment) {
        setError(t.addNote.contentRequired);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      
      // AI tag suggestion for add mode only - skip if user manually added tags
      let tagsToUse = tags;
      if (!isEdit && tags.length === 0) {
        if (!premium) {
          // AI auto-tagging is premium: free users add a tag themselves.
          setIsSubmitting(false);
          setPremiumDemo('ai_tagging');
          setError(t.addNote.tagRequiredFree);
          return;
        }
        if (!useAiTags) {
          // The switch is off, so the tag has to come from the user
          setIsSubmitting(false);
          setError(t.addNote.aiOffNeedsTag);
          return;
        }
        const suggestedTags = await suggestTagsWithAI();
        if (suggestedTags.length === 0) {
          // Error already set in suggestTagsWithAI
          setIsSubmitting(false);
          return;
        }
        setTags(suggestedTags);
        tagsToUse = suggestedTags; // Use the returned tags, not state
      }

      // Validate tags (use tagsToUse which has the latest tags). Not for a
      // note someone shared: its tags are the owner's and the API ignores
      // anything an editor sends for them.
      if (owned && (!tagsToUse || tagsToUse.length === 0)) {
        setError(t.addNote.tagRequired);
        setIsSubmitting(false);
        return;
      }

      let finalContent = editor?.getHTML() || '';

      // An image-only note says nothing in words, and now stores nothing:
      // it used to store the literal text "[[image]]" so the content field
      // had something in it.
      if (finalContent.trim() === '<p></p>') {
        finalContent = '';
      }

      // Existing tags are sent by ID so the note save can never resurrect a
      // renamed tag or revert color edits with the modal's (possibly stale)
      // copy; only brand-new tags (temporary Date.now() ids) send a dict for
      // the backend to create. Mirrors the id threshold used in TagInput.
      const tagPayload = tagsToUse.map(tag =>
        tag.id < 999999999999
          ? tag.id
          : { name: tag.name, color: tag.color, text_color: tag.text_color }
      );

      // Only send the file while it is still attached. If the user took it
      // off, we must not re-upload the stale `media` File that was loaded
      // for editing.
      const shouldSendFile = hasAttachment && !!media;
      const shouldDeleteMedia = isEdit && hadOriginalMedia && !hasAttachment;

      // Use FormData only when uploading a file
      if (shouldSendFile) {
        const formDataPayload = new FormData();
        formDataPayload.append('content', finalContent);
        formDataPayload.append('tags', JSON.stringify(tagPayload));
        if (baseUpdatedAtRef.current) {
          formDataPayload.append('base_updated_at', baseUpdatedAtRef.current);
        }

        const validLinks = links.filter(link => link.url.trim());
        if (validLinks.length > 0) {
          formDataPayload.append('links', JSON.stringify(validLinks));
        }

        formDataPayload.append('file', media);
        formDataPayload.append('media_type',
          isPdfFile(media) ? 'pdf' : media.type.startsWith('image/') ? 'image' : 'video');

        const url = isEdit ? `/entries/${noteId}/` : '/entries/';
        const method = isEdit ? api.put : api.post;

        const saved = await method(url, formDataPayload, {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        });
        baseUpdatedAtRef.current = saved?.data?.updated_at || baseUpdatedAtRef.current;
      } else {
        // Use JSON — either no media, or the image was removed and needs deletion
        const payload: any = {
          content: finalContent,
          tags: tagPayload
        };

        if (baseUpdatedAtRef.current) {
          payload.base_updated_at = baseUpdatedAtRef.current;
        }

        const validLinks = links.filter(link => link.url.trim());
        if (validLinks.length > 0) {
          payload.links = validLinks;
        }

        if (shouldDeleteMedia) {
          payload.delete_media = true;
        }

        const url = isEdit ? `/entries/${noteId}/` : '/entries/';
        const method = isEdit ? api.put : api.post;

        const saved = await method(url, payload, {
          headers: {
            'Content-Type': 'application/json',
          }
        });
        baseUpdatedAtRef.current = saved?.data?.updated_at || baseUpdatedAtRef.current;
      }

      await tagInputRef.current?.refreshRecentTags();
      
      onSuccess();
      handleClose();
    } catch (error: any) {
      const clash = conflictFrom(error);
      if (clash) {
        // Their version against this one. Nothing is written, and what the
        // user typed stays in the editor until they choose.
        setConflict(clash);
      } else {
        console.error('Failed to save note:', error);
        setError(error.response?.data?.detail || t.addNote.saveFailed);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isEdit) {
      setContent('');
      setTags([]);
      setLinks([]);
      editor?.commands.setContent('');
    }
    // Always, edit mode included: the next note to open here is a different
    // note, and it should not inherit this one's picture.
    clearAttachment();
    setHadOriginalMedia(false);
    setError(null);
    setIsSubmitting(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="add-note-modal-overlay">
        <div className="add-note-modal" onClick={(e) => e.stopPropagation()}>
          <div className="add-note-header">
            <div className="header-left">
              <button className="cancel-btn" onClick={handleClose}>
                {t.addNote.cancel}
              </button>
              {!readOnly && <div className='action-buttons'>
                {!media && !existingPdf ? (
                  <button
                    className="action-btn"
                    onClick={() => fileInputRef.current?.click()}
                    title={t.addNote.addAttachment}
                    aria-label={t.addNote.addAttachment}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                  </button>
                ) : pdfAttachment ? null : media && (
                  <>
                    <button
                      className="action-btn ocr-btn"
                      onClick={extractTextFromImage}
                      disabled={isExtractingText || !media.type.startsWith('image/')}
                      title={t.addNote.extractText}
                    >
                      {isExtractingText ? (
                        <span>...</span>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                      )}
                    </button>
                  </>
                )}

                <button
                  className="action-btn"
                  onClick={() => setIsLinkModalOpen(true)}
                  title={t.addNote.addSourceUrls}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                  </svg>
                  {links.length > 0 && <span className="badge">{links.length}</span>}
                </button>

                {/* Enabled only once the note actually points at a video */}
                <button
                  className="action-btn summarize-video-btn"
                  onClick={summarizeVideo}
                  disabled={!youtubeUrl || isSummarizingVideo}
                  title={youtubeUrl ? t.addNote.summarizeVideo : t.addNote.summarizeVideoDisabled}
                  aria-label={youtubeUrl ? t.addNote.summarizeVideo : t.addNote.summarizeVideoDisabled}
                >
                  {isSummarizingVideo ? (
                    <span className="summarize-spinner" />
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="5" width="14" height="14" rx="2" ry="2"></rect>
                      <polygon points="16 11 22 7 22 17 16 13" fill="currentColor" stroke="none"></polygon>
                      <polyline points="6 10 11 10"></polyline>
                      <polyline points="6 14 9 14"></polyline>
                    </svg>
                  )}
                </button>
              </div>}
            </div>
            <h2>{isEdit ? t.editNote.title : t.addNote.title}</h2>
            
            <div className="header-right">
              {/* Stays put while tags are added, rather than vanishing the
                  moment the first tag lands and making the header jump.
                  Free users see it held off; clicking shows what premium
                  unlocks, so it is aria-disabled rather than disabled. */}
              {!isEdit && (
                <div className="ai-suggest-toggle">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={premium && useAiTags}
                    aria-disabled={!premium}
                    className={`toggle-switch${premium && useAiTags ? ' is-on' : ''}${premium ? '' : ' is-locked'}`}
                    onClick={() => (premium
                      ? setUseAiTags(!useAiTags)
                      : setPremiumDemo('ai_tagging'))}
                    title={t.addNote.aiSuggest}
                  >
                    <span className="toggle-track"><span className="toggle-knob" /></span>
                    <span className="toggle-label">{t.addNote.aiSuggest}</span>
                  </button>
                  <div className="ai-info-tooltip">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M12 16v-4"></path>
                      <circle cx="12" cy="8" r="0.5" fill="currentColor"></circle>
                    </svg>
                    <div className="ai-info-tooltip-text">
                      {t.addNote.aiInfo}
                    </div>
                  </div>
                </div>
              )}
              {/* A viewer has nothing to save; the server would answer 403 */}
              {!readOnly && (
                <button
                  className="remember-btn"
                  onClick={handleSubmit}
                  disabled={isSubmitting || isGeneratingTags || (isEdit && isLoading)}
                >
                  {isGeneratingTags ? t.addNote.generatingTags : isSubmitting ? t.addNote.saving : (isEdit ? t.editNote.update : t.addNote.remember)}
                </button>
              )}
            </div>
          </div>

          <SharedBanner access={access} />

          {error && <div className="add-note-error">{error}</div>}

          <div className="add-note-body">
            {/* With something attached, the writing area gives up its empty
                floor so the attachment sits near the text it belongs with
                rather than below the fold under the tag field. */}
            <div className={`content-editor${hasAttachment ? ' has-attachment' : ''}`}>
              {!readOnly && <div className="editor-toolbar">
                <button
                  type="button"
                  className={`toolbar-btn${editor?.isActive('bulletList') ? ' is-active' : ''}`}
                  onClick={handleToggleBulletList}
                  title={t.editor.bulletList}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="9" y1="6" x2="20" y2="6"></line>
                    <line x1="9" y1="12" x2="20" y2="12"></line>
                    <line x1="9" y1="18" x2="20" y2="18"></line>
                    <circle cx="4" cy="6" r="1.5" fill="currentColor" stroke="none"></circle>
                    <circle cx="4" cy="12" r="1.5" fill="currentColor" stroke="none"></circle>
                    <circle cx="4" cy="18" r="1.5" fill="currentColor" stroke="none"></circle>
                  </svg>
                </button>
                <button
                  type="button"
                  className={`toolbar-btn${editor?.isActive('orderedList') ? ' is-active' : ''}`}
                  onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                  title={t.editor.numberedList}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="10" y1="6" x2="21" y2="6"></line>
                    <line x1="10" y1="12" x2="21" y2="12"></line>
                    <line x1="10" y1="18" x2="21" y2="18"></line>
                    <text x="2" y="8" fontSize="8" fill="currentColor" stroke="none" fontFamily="sans-serif">1.</text>
                    <text x="2" y="14" fontSize="8" fill="currentColor" stroke="none" fontFamily="sans-serif">2.</text>
                    <text x="2" y="20" fontSize="8" fill="currentColor" stroke="none" fontFamily="sans-serif">3.</text>
                  </svg>
                </button>
                <div className="toolbar-divider"></div>
                <button
                  type="button"
                  className={`toolbar-btn${editor?.isActive('bold') ? ' is-active' : ''}`}
                  onClick={() => editor?.chain().focus().toggleBold().run()}
                  title={t.editor.bold}
                >
                  <strong>B</strong>
                </button>
                <button
                  type="button"
                  className={`toolbar-btn${editor?.isActive('italic') ? ' is-active' : ''}`}
                  onClick={() => editor?.chain().focus().toggleItalic().run()}
                  title={t.editor.italic}
                >
                  <em>I</em>
                </button>
                <div className="toolbar-divider"></div>
                {/* Typing ``` already opens a code block, but nothing on
                    screen says so — this is the discoverable way in. */}
                <button
                  type="button"
                  className={`toolbar-btn${editor?.isActive('codeBlock') ? ' is-active' : ''}`}
                  onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
                  title={t.addNote.codeBlockHint}
                  aria-label={t.editor.codeBlock}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="8 7 3 12 8 17"></polyline>
                    <polyline points="16 7 21 12 16 17"></polyline>
                  </svg>
                </button>
                {/* $x$ and $$…$$ are the typed way in; this is the one people
                    can find. */}
                <button
                  type="button"
                  className="toolbar-btn"
                  onClick={() => editor && insertInlineMath(editor, t.addNote.latexInsertPrompt)}
                  title={t.addNote.equationHint}
                  aria-label={t.addNote.equation}
                >
                  <span style={{ fontFamily: 'serif', fontStyle: 'italic' }}>&Sigma;</span>
                </button>
              </div>}
              <EditorContent editor={editor} />

              {/* The attached picture, under the text it belongs with. The
                  frame takes the image's own proportions so the remove button
                  sits on the picture's corner rather than out in the empty
                  space beside a letterboxed one. */}
              {imagePreview && (
                <div className="composer-image">
                  <div
                    className="composer-image-frame"
                    style={imageAspect ? { aspectRatio: String(imageAspect) } : undefined}
                  >
                    <img
                      src={imagePreview}
                      alt={t.addNote.attachedAlt}
                      onLoad={(e) => {
                        const img = e.currentTarget;
                        if (img.naturalHeight) setImageAspect(img.naturalWidth / img.naturalHeight);
                      }}
                    />
                    <button
                      type="button"
                      className="composer-image-remove"
                      title={t.addNote.removeImage}
                      aria-label={t.addNote.removeImage}
                      onClick={clearAttachment}
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
              {pdfAttachment && (
                <div className="composer-pdf">
                  {/* A new upload has no URL yet; it is the user's own file */}
                  <button
                    type="button"
                    className="composer-pdf-file"
                    disabled={!existingPdf}
                    title={existingPdf ? t.note.openPdf : undefined}
                    onClick={() => existingPdf && openMedia(existingPdf.url).catch(err =>
                      console.error('Failed to open PDF:', err))}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                    </svg>
                    <span className="composer-pdf-name">{pdfAttachment.name}</span>
                    {pdfAttachment.size > 0 && (
                      <span className="composer-pdf-size">{formatBytes(pdfAttachment.size)}</span>
                    )}
                  </button>
                  {/* The file is the owner's: the API ignores a collaborator's delete */}
                  {owned && (
                    <button
                      type="button"
                      className="composer-pdf-remove"
                      title={t.addNote.removePdf}
                      aria-label={t.addNote.removePdf}
                      onClick={clearAttachment}
                    >
                      ×
                    </button>
                  )}
                </div>
              )}
              <div className="add-note-actions">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*,application/pdf,.pdf"
                    onChange={handleImageSelect}
                    style={{ display: 'none' }}
                  />
              </div>
            </div>
            {/* A collaborator's tags are ignored by the API — the tags on a
                note belong to whoever owns it. */}
            {owned && (
              <div className="tags-section">
                <TagInput tags={tags} setTags={setTags} ref={tagInputRef} />
              </div>
            )}
          </div>
        </div>
      </div>

      <LinkModal
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        links={links}
        setLinks={setLinks}
      />

      {conflict && (
        <ConflictDialog
          lastEditedBy={conflict.last_edited_by}
          busy={isSubmitting}
          onLoadTheirs={() => {
            const theirs = (conflict.content || '').replace(/\[\[image\]\]/g, '');
            editor?.commands.setContent(theirs, { emitUpdate: false });
            setContent(theirs);
            baseUpdatedAtRef.current = conflict.updated_at;
            setConflict(null);
          }}
          onKeepMine={() => {
            // Save again, now based on the version they wrote
            baseUpdatedAtRef.current = conflict.updated_at;
            setConflict(null);
            handleSubmit();
          }}
        />
      )}

      {premiumDemo && (
        <PremiumFeatureModal feature={premiumDemo} onClose={() => setPremiumDemo(null)} />
      )}
    </>
  );
};

export default NoteModal;
